import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Button,
  Paper,
  Typography,
  IconButton,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  Collapse
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Clear as ClearIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';

export interface LogEntry {
  rfpId: string;
  requestId: string;
  timestamp: string;
  level: string;
  message: string;
  source: 'stdout' | 'stderr';
}

interface LogViewerProps {
  logs: LogEntry[];
  onClear?: () => void;
  defaultExpanded?: boolean;
}

const LogViewer: React.FC<LogViewerProps> = ({
  logs,
  onClear,
  defaultExpanded = false
}) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [filter, setFilter] = useState<string>('all');
  const logsEndRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (autoScroll && logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, autoScroll]);

  const handleFilterChange = (_event: React.MouseEvent<HTMLElement>, newFilter: string | null) => {
    if (newFilter !== null) {
      setFilter(newFilter);
    }
  };

  const filteredLogs = logs.filter(log => {
    if (filter === 'all') return true;
    if (filter === 'info') return log.level === 'info';
    if (filter === 'debug') return log.level === 'debug';
    if (filter === 'error') return log.level === 'error' || log.source === 'stderr';
    return true;
  });

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    });
  };

  const getLogColor = (log: LogEntry) => {
    if (log.source === 'stderr' || log.level === 'error') return '#ff6b6b';
    if (log.level === 'debug') return '#868e96';
    return '#e0e0e0';
  };

  return (
    <Paper
      elevation={2}
      sx={{
        width: '100%',
        mb: 2,
        borderRadius: 2,
        overflow: 'hidden'
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          bgcolor: 'grey.900',
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'grey.800'
          }
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="h6" color="white">
            Logs
          </Typography>
          {logs.length > 0 && (
            <Typography variant="body2" color="grey.400">
              {filteredLogs.length} {filteredLogs.length === 1 ? 'entry' : 'entries'}
            </Typography>
          )}
        </Stack>
        <IconButton size="small" sx={{ color: 'white' }}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      {/* Collapsible Content */}
      <Collapse in={expanded}>
        {/* Controls */}
        {logs.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 2,
              bgcolor: 'grey.50',
              borderBottom: '1px solid',
              borderColor: 'divider'
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <FilterListIcon sx={{ color: 'grey.600' }} />
              <ToggleButtonGroup
                value={filter}
                exclusive
                onChange={handleFilterChange}
                size="small"
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="info">Info</ToggleButton>
                <ToggleButton value="debug">Debug</ToggleButton>
                <ToggleButton value="error">Error</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                size="small"
                variant={autoScroll ? 'contained' : 'outlined'}
                onClick={() => setAutoScroll(!autoScroll)}
                sx={{ textTransform: 'none' }}
              >
                Auto-scroll: {autoScroll ? 'On' : 'Off'}
              </Button>
              {onClear && (
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<ClearIcon />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClear();
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  Clear
                </Button>
              )}
            </Stack>
          </Box>
        )}

        {/* Log Display */}
        <Box
          sx={{
            bgcolor: '#1e1e1e',
            maxHeight: '400px',
            overflowY: 'auto',
            fontFamily: 'monospace',
            fontSize: '13px',
            p: 2
          }}
        >
          {filteredLogs.length === 0 ? (
            <Typography variant="body2" color="grey.500" sx={{ fontFamily: 'monospace' }}>
              {logs.length === 0 ? 'No logs yet...' : 'No logs matching filter...'}
            </Typography>
          ) : (
            filteredLogs.map((log, index) => (
              <Box
                key={`${log.requestId}-${index}`}
                sx={{
                  mb: 0.5,
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    color: getLogColor(log),
                    fontSize: 'inherit',
                    fontFamily: 'inherit'
                  }}
                >
                  <Typography
                    component="span"
                    sx={{
                      color: '#95a5a6',
                      mr: 1
                    }}
                  >
                    [{formatTimestamp(log.timestamp)}]
                  </Typography>
                  <Typography
                    component="span"
                    sx={{
                      color: log.source === 'stderr' ? '#e74c3c' : '#3498db',
                      mr: 1,
                      fontWeight: 'bold'
                    }}
                  >
                    [{log.source}]
                  </Typography>
                  {log.message}
                </Typography>
              </Box>
            ))
          )}
          <div ref={logsEndRef} />
        </Box>
      </Collapse>
    </Paper>
  );
};

export default LogViewer;
